import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Grid,
  Typography,
  Link,
  IconButton,
  Button,
  useTheme,
  useMediaQuery,
  Divider,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  Facebook,
  Twitter,
  Instagram,
  LinkedIn,
  LocationOn,
  Phone,
  Email,
  Restaurant,
  Download,
  WhatsApp,
  ArrowForward
} from '@mui/icons-material';
import api from '../../services/api';
import menuCardService from '../../services/menuCardService';
import { useRestaurant } from '../../contexts/RestaurantContext';
import { restaurantService } from '../../services/restaurantService';
import { useNotification } from '../../contexts/NotificationContext';

import outletService from '../../services/outletService';

// import {getOutlet} from '../../services/outletService';

const footerColor = {
  bg: '#351113',
  text: '#FFCF03',
  white: '#FFFFF0',
  hover: '#351113E5'

}

const Footer = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { showSuccess, showError } = useNotification();
  const {
    selectedOutlet,
    menuItems,
    categories,
    loading: restaurantLoading
  } = useRestaurant();

  const [restaurantData, setRestaurantData] = useState(null);
  const [outletData, setOutletData] = useState([])
  const [socialLinks, setSocialLinks] = useState({
    facebook: '',
    instagram: '',
    whatsapp: '',
    tiktok: '',
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [downloadingPDF, setDownloadingPDF] = useState(false);

  const currentYear = new Date().getFullYear();

  useEffect(() => {
    const fetchRestaurantData = async () => {
      try {
        setLoading(true);
        const [restaurantResponse, socialResponse, outletResponse] = await Promise.all([
          api.get('/public/restaurant'),
          api.get('/public/social-links').catch(() => ({ data: null })), // Don't fail if social links don't exist
          restaurantService.getPublicOutlets()
        ]);
        setRestaurantData(restaurantResponse.data);
        setOutletData(outletResponse);
        if (socialResponse.data) {
          setSocialLinks(socialResponse.data);
        }
        setError(null);
      } catch (err) {
        console.error('Error fetching restaurant data:', err);
        setError('Failed to load restaurant information');
        // Set fallback data
        setRestaurantData({
          name: 'Restaurant Name',
          description: 'Authentic Indian cuisine served with love and tradition. Experience the rich flavors of India in every bite.',
          phone: '+1 (555) 123-4567',
          email: 'info@restaurant.com',
          website: null
        });
      } finally {
        setLoading(false);
      }
    };

    fetchRestaurantData();
  }, []);

  const handleDownloadMenuPDF = async () => {
    try {
      setDownloadingPDF(true);

      if (!selectedOutlet) {
        showError('Please select an outlet first');
        return;
      }

      if (!menuItems || menuItems.length === 0) {
        showError('No menu items available for download');
        return;
      }

      console.log('Downloading menu PDF for outlet:', selectedOutlet);
      console.log('Menu items:', menuItems);
      console.log('Menu items length:', menuItems?.length);
      console.log('Categories:', categories);
      console.log('Categories length:', categories?.length);


      // Debug: Check first few menu items
      if (menuItems && menuItems.length > 0) {
        console.log('First menu item:', menuItems[0]);
        console.log('Menu item price type:', typeof menuItems[0]?.price);
        console.log('Menu item price value:', menuItems[0]?.price);
        console.log('Menu item base_price:', menuItems[0]?.base_price);
        console.log('Menu item addons:', menuItems[0]?.addons);
        console.log('Menu item sizes:', menuItems[0]?.sizes);
        console.log('Menu item keys:', Object.keys(menuItems[0] || {}));
      }

      // Generate PDF
      const pdfBlob = await menuCardService.generateMenuCardPDF(
        selectedOutlet,
        menuItems,
        categories
      );

      // Download PDF
      const filename = `Menu_${selectedOutlet.name.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`;
      menuCardService.downloadPDF(pdfBlob, filename);

      showSuccess('Menu PDF downloaded successfully!');
    } catch (error) {
      console.error('Error downloading menu PDF:', error);
      showError(`Failed to download menu PDF: ${error.message}`);
    } finally {
      setDownloadingPDF(false);
    }
  };

  return (
    <Box
      component="footer"
      sx={{
        bgcolor: theme.palette.mode === 'dark' ? 'grey.900' : 'grey.100',
        color: theme.palette.mode === 'dark' ? 'grey.100' : 'grey.800',
        mt: 'auto',
        py: { xs: 4, md: 6 },
        borderTop: '3px solid',
        borderColor: 'primary.main',
        boxShadow: '0 -4px 8px rgba(0,0,0,0.1)',
        position: 'relative',
        zIndex: 1,
      }}
    >
      <Container maxWidth="lg">
        <Grid container spacing={4}>
          {/* Restaurant Info */}
          <Grid item xs={12} sm={6} md={3}>
            <Box sx={{ mb: 2, textAlign: 'center', width: '247px' }}>
              {/* <Restaurant sx={{ fontSize: 32, color: 'primary.main', mb: 1 }} /> */}
              <Box component="img" src={restaurantData?.logo_url}
                sx={{
                  textAlign: 'center',
                  width: { xs: '25%', sm: '25%', md: '25%' },
                  height: { xs: '25', sm: '25', md: '25' },
                  borderRadius: '50%'
                }}
              ></Box>
              {loading ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={20} />
                  <Typography variant="body2">Loading...</Typography>
                </Box>
              ) : error ? (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              ) : (
                <>
                  {/* <Typography variant="h6" fontWeight="bold" gutterBottom>
                    {restaurantData?.name || 'Restaurant Name'}
                  </Typography> */}
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {restaurantData?.description || 'Authentic Indian cuisine served with love and tradition. Experience the rich flavors of India in every bite.'}
                  </Typography>
                </>
              )}
            </Box>
          </Grid>

          {/* Quick Links */}
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              Quick Links
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Link href="/" color="inherit" underline="hover">
                Home
              </Link>
              <Link href="/delivery" color="inherit" underline="hover">
                Delivery
              </Link>
              <Link href="/takeaway" color="inherit" underline="hover">
                Takeaway
              </Link>
              <Link href="/table-orders" color="inherit" underline="hover">
                Table Orders
              </Link>
              <Link href="/catering" color="inherit" underline="hover">
                Catering
              </Link>

              {/* Download Menu PDF Button */}
              <Box sx={{ mt: 2 }}>
                <Button
                  variant="outlined"
                  color="primary"
                  startIcon={downloadingPDF ? <CircularProgress size={16} /> : <Download />}
                  onClick={handleDownloadMenuPDF}
                  disabled={downloadingPDF || !selectedOutlet || !menuItems || menuItems.length === 0}
                  sx={{
                    width: '100%',
                    justifyContent: 'flex-start',
                    textTransform: 'none',
                    fontWeight: 'normal',
                    '&:hover': {
                      bgcolor: 'primary.50',
                    },
                  }}
                >
                  {downloadingPDF ? 'Downloading...' : 'Download Menu PDF'}
                </Button>
                {!selectedOutlet && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                    Select an outlet to download menu
                  </Typography>
                )}
              </Box>
            </Box>
          </Grid>

          {/* Contact Info */}
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              Contact Us
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{
                  display: 'flex',
                  flexDirection: 'column',
                }}>
                  {outletData && outletData.map((outlet) => {
                    return (
                      <>
                        <Box sx={{
                          marginTop: '8px',
                          // borderBottom: '2px solid',
                          // borderImage: 'linear-gradient(90deg, #891313, #ECD73C, #891313) 1',

                        }}>


                          {/* <LocationOn sx={{ fontSize: 20, color: 'primary.main' }} /> */}
                          <Typography variant="body2"
                            sx={{
                              textAlign: 'left',
                              fontSize: { xs: '1rem', sm: '1rem', md: '12px' },
                              fontWeight: 700
                            }}>{outlet?.name}</Typography>

                          <Typography variant="body2" sx={{ textAlign: 'left', fontSize: { md: '12px !important' } }}>

                            {outlet?.address}</Typography>
                          <Typography variant="body2" sx={{ textAlign: 'left', fontSize: { md: '12px !important' }, mb: 2 }}>
                            <Phone sx={{ marginRight: { md: '2px' }, fontSize: { md: '12px !important' } }} />
                            {outlet?.phone}</Typography>
                          <Typography variant="h6" fontWeight="bold" gutterBottom
                            sx={{
                              width: '50%',
                              fontSize: { md: '12px !important' },
                              fontWeight: '700',
                              pb: 1
                            }}>
                            Timing
                          </Typography>
                          <Typography variant="body2" sx={{ textAlign: 'left', fontSize: { md: '12px !important' }, mb: 2 }}>Lunch : 11:00AM to 03.00PM</Typography>
                          <Typography variant="body2" sx={{ textAlign: 'left', fontSize: { md: '12px !important' }, mb: 2 }}>Dinner : 05:00AM to 11.00PM</Typography>
                        </Box>
                      </>
                    )
                  })}


                </Box>
                {/* <LocationOn sx={{ fontSize: 20, color: 'primary.main' }} />
                <Typography variant="body2">
                  {outletData}
                  {restaurantData?.address || '123 Main Street, City, State 12345'}
                </Typography> */}
              </Box>
              {/* {restaurantData?.phone && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Phone sx={{ fontSize: 20, color: 'primary.main' }} />
                  <Typography variant="body2">
                    {restaurantData.phone}
                  </Typography>
                </Box>
              )} */}
              {/* {restaurantData?.email && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Email sx={{ fontSize: 20, color: 'primary.main' }} />
                  <Typography variant="body2">
                    {restaurantData.email}
                  </Typography>
                </Box>
              )} */}
              {/* {restaurantData?.website && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2">
                    <Link href={restaurantData.website} target="_blank" rel="noopener noreferrer" color="inherit">
                      Visit Website
                    </Link>
                  </Typography>
                </Box>
              )} */}
            </Box>
          </Grid>



          {/* Social Media */}
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              Follow Us
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
              {socialLinks.facebook && (
                <IconButton
                  color="primary"
                  href={socialLinks.facebook}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    bgcolor: '#1877F2',
                    color: 'white',
                    '&:hover': {
                      bgcolor: '#166FE5',
                    },
                  }}
                >
                  <Facebook />
                </IconButton>
              )}
              {socialLinks.instagram && (
                <IconButton
                  color="primary"
                  href={socialLinks.instagram}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    bgcolor: '#E4405F',
                    color: 'white',
                    '&:hover': {
                      bgcolor: '#D62976',
                    },
                  }}
                >
                  <Instagram />
                </IconButton>
              )}
              {socialLinks.whatsapp && (
                <IconButton
                  color="primary"
                  href={socialLinks.whatsapp}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    bgcolor: '#25D366',
                    color: 'white',
                    '&:hover': {
                      bgcolor: '#128C7E',
                    },
                  }}
                >
                  <WhatsApp />
                </IconButton>
              )}
              {socialLinks.tiktok && (
                <IconButton
                  color="primary"
                  href={socialLinks.tiktok}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    bgcolor: '#000000',
                    color: 'white',
                    '&:hover': {
                      bgcolor: '#333333',
                    },
                  }}
                >
                  <Typography variant="body2" fontWeight="bold">TT</Typography>
                </IconButton>
              )}
            </Box>
            <Typography variant="body2" color="text.secondary">
              Stay updated with our latest offers and news
            </Typography>
          </Grid>
        </Grid>

        <Divider sx={{ my: 3 }} />

        {/* Copyright */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <Typography variant="body2" color="text.secondary">
            © {currentYear} {restaurantData?.name || 'Restaurant Name'}. All rights reserved.
          </Typography>
          <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
            <Link
              href="/about"
              color="inherit"
              underline="hover"
              variant="body2"
              target="_blank"
              rel="noopener noreferrer"
            >
              About Us
            </Link>
            <Link
              href="/contact"
              color="inherit"
              underline="hover"
              variant="body2"
              target="_blank"
              rel="noopener noreferrer"
            >
              Contact Us
            </Link>
            <Link
              href="/privacy"
              color="inherit"
              underline="hover"
              variant="body2"
              target="_blank"
              rel="noopener noreferrer"
            >
              Privacy Policy
            </Link>
            <Link
              href="/terms"
              color="inherit"
              underline="hover"
              variant="body2"
              target="_blank"
              rel="noopener noreferrer"
            >
              Terms of Service
            </Link>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default Footer;
